
import type { PropType, SetupContext } from 'vue'
import type { FacetItem } from '../../models'
import Sortable from 'sortablejs'

import { defineComponent, onMounted, ref, watch } from 'vue'
import { capitalizeSentence } from '../../filters'
import { EpbButton } from '../shared/epb-button'
import { EpbText } from '../shared/epb-text'

export default defineComponent({
  name: 'EpbFacetsSettingsSection',
  components: { EpbButton, EpbText },
  props: {
    items: {
      type: Array as PropType<Array<FacetItem>>,
      required: true,
    },
    facetsOrder: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    draggable: Boolean,
    editable: Boolean,
  },
  setup(props, ctx: SetupContext) {
    const baseClass = 'epb-facets-settings-section'

    const { emit } = ctx

    const container = ref(null)

    const facetItems = ref(props.items)

    const facetItemsOrder = ref(props.facetsOrder)
    const getFacetPosition = (title: string) => facetItemsOrder.value.indexOf(title) + 1

    const setFacetPosition = (event: Event, title: string) => {
      const value = Number.parseInt((event.target as HTMLInputElement).value)
      const currentIndex = facetItemsOrder.value.indexOf(title)

      requestAnimationFrame(() => {
        const itemElement = container.value?.querySelectorAll(`.${baseClass}__item`)[value - 1]
        if (itemElement) {
          itemElement.classList.add(`${baseClass}__item--move`)

          itemElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

          setTimeout(() => {
            itemElement.classList.remove(`${baseClass}__item--move`)
          }, 500)
        }
      })

      if (currentIndex !== -1 && value) {
        facetItemsOrder.value.splice(currentIndex, 1)
        facetItemsOrder.value.splice(value - 1, 0, title)

        emit('orderFacets', facetItemsOrder.value)
      }
    }

    const facetTitle = (item: FacetItem): string => {
      return item.prettyName === undefined || item.prettyName === ''
        ? capitalizeSentence(item.title)
        : capitalizeSentence(item.prettyName)
    }

    const selectItem = (item: FacetItem): void => {
      const indexToUpdate = facetItems.value.findIndex(
        facetItem => facetItem.title === item.title && facetItem.subtitle === item.subtitle,
      )

      if (indexToUpdate !== -1) {
        const updatedItem = { ...facetItems.value[indexToUpdate], selected: !item.selected }
        facetItems.value.splice(indexToUpdate, 1, updatedItem)
        emit('selectItem', updatedItem)
      }

      facetItems.value.map(facetItem => {
        if (facetItem !== facetItems.value[indexToUpdate]) {
          facetItem.selected = false
          return facetItem
        }
      })
    }

    onMounted(() => {
      new Sortable(container.value, {
        animation: 150,
        onEnd: ({ oldIndex, newIndex }) => {
          facetItemsOrder.value = []
          const itemToMove = facetItems.value.splice(oldIndex, 1)[0]
          facetItems.value.splice(newIndex, 0, itemToMove)
          facetItems.value.forEach(newFacetItem => facetItemsOrder.value.push(newFacetItem.title))
          emit('orderFacets', facetItemsOrder.value)
        },
      })
    })

    watch(
      () => props.items,
      newVal => {
        facetItems.value = newVal
      },
    )

    function sortFacetItems(facetItemsToOrder: FacetItem[], facetItemsOrder: string[]): void {
      const orderMap = new Map(facetItemsOrder.map((title, index) => [title.toLowerCase(), index]))

      facetItems.value = facetItemsToOrder.sort((a, b) => {
        const indexA = orderMap.get(a.title.toLowerCase()) ?? Infinity
        const indexB = orderMap.get(b.title.toLowerCase()) ?? Infinity
        return indexA - indexB
      })
    }

    watch(
      () => props.facetsOrder,
      newVal => {
        facetItemsOrder.value = newVal.length
          ? newVal
          : facetItems.value.map(facetItem => facetItem.title)
        sortFacetItems(facetItems.value, facetItemsOrder.value)
      },
    )

    return {
      baseClass,
      container,
      facetItems,
      getFacetPosition,
      setFacetPosition,
      facetTitle,
      selectItem,
    }
  },
})
